import React, { useEffect, useState } from 'react';
import { api, Post, User } from '../lib/api';
import { ContentCard } from '../components/ContentCard';

interface ExploreProps {
  user: User | null;
}

export const Explore: React.FC<ExploreProps> = ({ user }) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const fetchPosts = async (pageNumber: number, replace = false) => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getPosts(pageNumber, 12);
      
      if (replace) {
        setPosts(res.posts);
      } else {
        setPosts((prev) => {
          // Remove duplicates
          const existingIds = new Set(prev.map(p => p.id));
          const newPosts = res.posts.filter(p => !existingIds.has(p.id));
          return [...prev, ...newPosts];
        });
      }
      
      setHasMore(res.posts.length === 12);
    } catch (err: any) {
      console.error('Failed to fetch posts feed', err);
      setError(err.message || 'Failed to load feed.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts(1, true);
  }, [user]); // Re-fetch feed when user auth status changes (locks/unlocks resolve differently)

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPosts(nextPage);
  };

  const handlePostDeleted = (deletedId: string) => {
    setPosts(prev => prev.filter(p => p.id !== deletedId));
  };

  // Filter posts based on search query (by creator name or caption)
  const filteredPosts = posts.filter((post) => {
    const name = (post.creator_name || '').toLowerCase();
    const caption = (post.caption || '').toLowerCase();
    const query = searchQuery.toLowerCase();
    return name.includes(query) || caption.includes(query);
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
      
      {/* Search Header */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '1rem',
        alignItems: 'center',
        textAlign: 'center',
        padding: '1rem 0'
      }}>
        <h2 style={{ fontSize: '2rem', fontFamily: 'var(--font-heading)' }}>Explore Soles 👣</h2>
        <p className="text-secondary">Discover exclusive content from verified creators.</p>
        
        <div style={{ position: 'relative', width: '100%', maxWidth: '500px', marginTop: '0.5rem' }}>
          <input 
            type="text" 
            className="form-input" 
            style={{ width: '100%', paddingLeft: '2.75rem', borderRadius: '9999px' }}
            placeholder="Search by creator or caption..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <span style={{ 
            position: 'absolute', 
            left: '1rem', 
            top: '50%', 
            transform: 'translateY(-50%)', 
            fontSize: '1.2rem',
            color: 'var(--text-muted)'
          }}>
            🔍
          </span>
        </div>
      </div>

      {/* Main feed */}
      {error && (
        <div className="glass-card" style={{ 
          background: 'rgba(239, 68, 68, 0.05)', 
          borderColor: 'rgba(239, 68, 68, 0.2)',
          textAlign: 'center', 
          padding: '2rem' 
        }}>
          <p style={{ color: '#ef4444' }}>⚠️ {error}</p>
          <button 
            className="btn btn-secondary btn-sm" 
            style={{ marginTop: '1rem' }} 
            onClick={() => { setPage(1); fetchPosts(1, true); }}
          >
            Try Again
          </button>
        </div>
      )}

      {!error && filteredPosts.length === 0 && !loading && (
        <div className="glass-card" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👣📭</div>
          <h3>No soles found</h3>
          <p className="text-muted" style={{ marginTop: '0.5rem' }}>
            {searchQuery ? 'Try matching a different query or username.' : 'Creators haven\'t posted anything yet.'}
          </p>
        </div>
      )}

      {filteredPosts.length > 0 && (
        <div className="explore-grid">
          {filteredPosts.map((post) => (
            <ContentCard 
              key={post.id} 
              post={post} 
              currentUserId={user?.id}
              onDelete={handlePostDeleted}
            />
          ))}
        </div>
      )}

      {/* Loading Skeletons */}
      {loading && (
        <div className="explore-grid">
          {Array(3).fill(0).map((_, idx) => (
            <div key={idx} className="glass-card shimmer" style={{ height: '400px' }} />
          ))}
        </div>
      )}

      {/* Load More Button */}
      {hasMore && !loading && !error && filteredPosts.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.5rem' }}>
          <button className="btn btn-secondary" onClick={handleLoadMore}>
            Load More Soles 🦶
          </button>
        </div>
      )}

    </div>
  );
};
